import { Entity, Input, Var } from "addeus-common-library/stores/firestore";
import {
    Collection,
    SubCollection,
} from "addeus-common-library/stores/firestore/collection";
import {
    SecurityCollectionGroup,
    SecurityEntity,
    SecuritySubCollection,
} from "addeus-common-library/stores/firestore/security/securityDecorators";
import moment from "moment-with-locales-es6";
import { Icon } from "./icon";
import "./profile";
import type { Profile } from "./profile";
import "./userMetadata";
import type { UserMetadata } from "./userMetadata";

export enum WorkspaceType {
    Profile = "profile",
    Folder = "folder",
    Group = "group",
}

const isAdmin =
    "get(/databases/$(database)/documents/UserMetadata/$(request.auth.uid)).data.role == 'admin'";

@Collection({
    namespace: "Workspace",
})
@SecurityEntity({
    get: `${isAdmin} || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))
        || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid))`,
    list: isAdmin,

    create: isAdmin,
    update: `${isAdmin} || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))`,
    delete: isAdmin,
})
export class Workspace extends Entity {
    @Var(String)
    @Input("text", {
        required: true,
    })
    name: string = "";

    isNew: boolean = false;

    @Var(Boolean)
    hasTemplateManagement: boolean = false;

    @Input("radio", {
        options: WorkspaceType,
        required: true,
    })
    @Var(WorkspaceType)
    type?: WorkspaceType;

    @Var(moment)
    createdAt: moment = moment();

    @Var(moment)
    deletedAt: moment | null = null;

    @Var(Icon)
    icon?: Icon;

    @Var(String)
    note?: string;

    @Collection<Profile>({
        namespace: "Profile",
    })
    @SecuritySubCollection({
        get: `${isAdmin} || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))
            || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid))`,
        list: `${isAdmin} || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid)) || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid))`,
        create: `${isAdmin} || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))`,
        update: `${isAdmin} || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))`,
        delete: isAdmin,
    })
    @SecurityCollectionGroup({
        get: isAdmin,
        list: isAdmin,
        create: isAdmin,
        update: isAdmin,
        delete: isAdmin,
    })
    profiles = new SubCollection<Profile>();

    @Collection<UserMetadata>({
        namespace: "UserMetadata",
        backlistFields: {
            ownerWorkspaces: true,
            userWorkspaces: true,
            ownerProfiles: true,
            userProfiles: true,
        },
    })
    @SecuritySubCollection({
        get: `exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid)) || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid)) || ${isAdmin}`,
        list: `exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid)) || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid)) || ${isAdmin}`,
        create: isAdmin,
        update: isAdmin,
        delete: `exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid)) || ${isAdmin}`,
    })
    owners = new SubCollection<UserMetadata>();

    /** @Todo sync workspace user with usermetadata userWorkspace if it reuse **/
    @Collection<UserMetadata>({
        namespace: "UserMetadata",
        backlistFields: {
            ownerWorkspaces: true,
            userWorkspaces: true,
            ownerProfiles: true,
            userProfiles: true,
        },
    })
    @SecuritySubCollection({
        get: `exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid)) || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid)) || ${isAdmin}`,
        list: `exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid)) || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid)) || ${isAdmin}`,
        create: isAdmin,
        update: isAdmin,
        delete: `exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid)) || ${isAdmin}`,
    })
    users = new SubCollection<UserMetadata>();

    get isDeleted() {
        return this.deletedAt !== null;
    }
}
